import { isReviewer } from "@/lib/perms";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { forceSimulation, forceLink, forceManyBody, forceCenter, forceCollide } from "d3-force";
import { useWorld } from "@/context/WorldContext";
import { fileUrl } from "@/lib/api";
import { REL_TYPES } from "@/lib/constants";
import { dedupeRels } from "@/lib/infobox";
import { RelationshipForm } from "@/components/RelationshipForm";
import { useOrigin } from "@/components/Origin";

const W = 900, H = 560, R = 28;

export default function RelationshipMap() {
  const { pages: allPages, relationships, basePath, readOnly, world } = useWorld();
  const { pages, toggle, isOC } = useOrigin();
  const nav = useNavigate();
  const svg = useRef(null);
  const drag = useRef(null);
  const [pos, setPos] = useState({});
  const [hidden, setHidden] = useState([]);
  const chars = useMemo(() => pages.filter((p) => p.category === "Characters"), [pages]);
  const ids = new Set(chars.map((c) => c.id));
  const allRels = relationships.filter((r) => ids.has(r.source_id) && ids.has(r.target_id));
  const rels = dedupeRels(allRels);
  const otpBy = Object.fromEntries(allPages.filter((p) => p.category === "Ships").map((p) => [p.id, p.otp_by || []]));
  const isOtpLine = (r) => (otpBy[r.ship_id] || []).some((id) => id === r.source_id || id === r.target_id);
  const key = chars.map((c) => c.id).join() + rels.map((r) => r.id).join();

  useEffect(() => {
    const nodes = chars.map((c) => ({ id: c.id }));
    const links = rels.map((r) => ({ source: r.source_id, target: r.target_id }));
    const sim = forceSimulation(nodes).force("link", forceLink(links).id((d) => d.id).distance(170))
      .force("charge", forceManyBody().strength(-600)).force("center", forceCenter(W / 2, H / 2)).force("collide", forceCollide(R * 2)).stop();
    for (let i = 0; i < 300; i++) sim.tick();
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
    setPos(Object.fromEntries(nodes.map((n) => [n.id, { x: clamp(n.x, 80, W - 80), y: clamp(n.y, R + 10, H - R - 24) }])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const toSvg = (e) => {
    const pt = svg.current.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    return pt.matrixTransform(svg.current.getScreenCTM().inverse());
  };
  const onMove = (e) => {
    if (!drag.current) return;
    drag.current.moved = true;
    const p = toSvg(e);
    setPos((s) => ({ ...s, [drag.current.id]: { x: p.x, y: p.y } }));
  };
  const onUp = () => {
    if (drag.current && !drag.current.moved) nav(`${basePath}/p/${drag.current.id}`);
    drag.current = null;
  };

  return (
    <div className="px-6 md:px-12 py-10 max-w-6xl space-y-8" data-testid="relationship-map-page">
      <div>
        <p className="font-code text-xs uppercase tracking-[0.3em] text-amber-500/80 mb-2">Constellation</p>
        <h1 className="font-display text-4xl sm:text-5xl text-amber-50">Bonds & blood-feuds</h1>
        <p className="text-sm text-slate-500 mt-2">Drag characters to arrange them. Click to open their page.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2" data-testid="rel-type-filters">
        {toggle}
        {Object.entries(REL_TYPES).map(([t, c]) => (
          <button key={t} onClick={() => setHidden((h) => (h.includes(t) ? h.filter((x) => x !== t) : [...h, t]))} data-testid={`rel-filter-${t}`}
            className={`flex items-center gap-1.5 text-xs font-code uppercase tracking-wider rounded-full border px-3 py-1 transition-opacity ${hidden.includes(t) ? "opacity-35 border-line" : "border-line bg-panel"}`}>
            <span className="w-2 h-2 rounded-full" style={{ background: c }} /> {t}
          </button>
        ))}
      </div>
      <div className="rounded-2xl border border-line bg-inset overflow-hidden bg-[radial-gradient(#282d3c_1px,transparent_1px)] [background-size:22px_22px]">
        {chars.length === 0 ? (
          <p className="p-16 text-center text-slate-500 italic" data-testid="map-empty">Create pages in the Characters category to see them here.</p>
        ) : (
          <svg ref={svg} viewBox={`0 0 ${W} ${H}`} className="w-full h-auto select-none touch-none" onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={() => (drag.current = null)} data-testid="relationship-graph">
            <defs>{chars.filter((c) => c.image).map((c) => (
              <pattern key={c.id} id={`img-${c.id}`} width="1" height="1" patternContentUnits="objectBoundingBox">
                <image href={fileUrl(c.image)} width="1" height="1" preserveAspectRatio="xMidYMid slice" />
              </pattern>
            ))}</defs>
            {rels.filter((r) => !hidden.includes(r.type)).map((r, idx, shown) => {
              const a = pos[r.source_id], b = pos[r.target_id];
              if (!a || !b) return null;
              const pair = [r.source_id, r.target_id].sort().join();
              const lane = shown.slice(0, idx).filter((o) => [o.source_id, o.target_id].sort().join() === pair).length;
              return (
                <g key={r.id}>
                  <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={REL_TYPES[r.type]} strokeWidth="2" strokeOpacity="0.7" strokeDasharray={r.type === "enemy" || r.type === "rival" ? "6 5" : undefined} />
                  <text x={(a.x + b.x) / 2} y={(a.y + b.y) / 2 - 6 + lane * 14} textAnchor="middle" fill={REL_TYPES[r.type]} fontSize="11" fontFamily="JetBrains Mono">
                    {isOtpLine(r) && <tspan fill="#fbbf24" data-testid={`map-otp-star-${r.id}`}>★ </tspan>}
                    {r.type === "ship" ? r.description : r.endgame ? "endgame" : r.type}
                  </text>
                </g>
              );
            })}
            {chars.map((c) => {
              const p = pos[c.id];
              if (!p) return null;
              return (
                <g key={c.id} transform={`translate(${p.x},${p.y})`} className="cursor-grab" data-testid={`graph-node-${c.id}`}
                  onPointerDown={(e) => { e.preventDefault(); drag.current = { id: c.id, moved: false }; }}>
                  <circle r={R + 4} fill="none" style={{ stroke: isOC(c) ? "rgb(var(--violet-400))" : "rgb(var(--amber-500))" }} strokeOpacity={isOC(c) ? 0.8 : 0.25} strokeDasharray={isOC(c) ? "4 3" : undefined} />
                  <circle r={R} style={{ fill: c.image ? `url(#img-${c.id})` : "rgb(var(--c-raised))", stroke: isOC(c) ? "rgb(var(--violet-400))" : "rgb(var(--amber-500))" }} strokeWidth="1.5" />
                  {!c.image && <text textAnchor="middle" dy="6" style={{ fill: "rgb(var(--amber-200))" }} fontSize="16" fontFamily="Cormorant Garamond">{c.title.split(" ").map((w) => w[0]).slice(0, 2).join("")}</text>}
                  <text y={R + 18} textAnchor="middle" style={{ fill: "rgb(var(--slate-200))" }} fontSize="13" fontFamily="Plus Jakarta Sans">{c.title}</text>
                  {isOC(c) && <text y={-R - 9} textAnchor="middle" style={{ fill: "rgb(var(--violet-300))" }} fontSize="9" fontFamily="JetBrains Mono" letterSpacing="1.5" data-testid={`graph-oc-${c.id}`}>ORIGINAL</text>}
                </g>
              );
            })}
          </svg>
        )}
      </div>
      {!readOnly && (isReviewer(world) || (world.my_role === "editor" && world.my_scope?.all && world.owner_pro)) && <RelationshipForm chars={allPages.filter((p) => p.category === "Characters")} rels={relationships} />}
    </div>
  );
}
